import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Download, FileSpreadsheet, Upload, X } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  VenueImportBatch,
  VenueImportClassification,
  VenueImportRow,
} from '../../types/domain';

const classificationLabels: Record<VenueImportClassification, string> = {
  valid: 'Valid',
  invalid: 'Invalid',
  exact_duplicate: 'Exact duplicate',
  possible_duplicate: 'Possible duplicate',
  conflict: 'Conflict',
};

function issueText(row: VenueImportRow) {
  const errors = Array.isArray(row.errors)
    ? row.errors
    : Object.entries(row.errors).flatMap(([field, messages]) => messages.map((message) => `${field}: ${message}`));
  const matches = row.matches.map((match) => {
    const target = match.name ?? (match.preview_row ? `CSV row ${match.preview_row}` : 'Existing store');
    const distance = match.distance_m === undefined ? '' : ` · ${match.distance_m} m away`;
    return `${target}${distance}`;
  });
  return [...errors, ...matches].join('; ') || 'Ready to import';
}

export function VenueImportPanel({ accessToken, onClose }: { accessToken: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<VenueImportBatch | null>(null);
  const [classification, setClassification] = useState<VenueImportClassification | ''>('');
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const imports = useApiCollection<VenueImportBatch>(
    'venue-imports', '/venue-imports/?page_size=10', accessToken,
  );

  const rowParams = new URLSearchParams({ page: String(page), page_size: '100' });
  if (classification) rowParams.set('classification', classification);
  const rows = useApiCollection<VenueImportRow>(
    'venue-import-rows',
    `/venue-imports/${batch?.id}/rows/?${rowParams.toString()}`,
    accessToken,
    Boolean(batch),
  );
  const visibleRows = rows.data?.results ?? [];

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Select a CSV file first.');
      const body = new FormData();
      body.append('file', file);
      return apiRequest<VenueImportBatch>('/venue-imports/preview/', {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
        body,
      });
    },
    onSuccess: (preview) => {
      setBatch(preview);
      setSelectedRows(new Set());
      setClassification('');
      setPage(1);
      void queryClient.invalidateQueries({ queryKey: ['collection', 'venue-imports'] });
    },
  });
  const confirmMutation = useMutation({
    mutationFn: () => apiRequest<VenueImportBatch>(`/venue-imports/${batch?.id}/confirm/`, {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({ rows: Array.from(selectedRows) }),
    }),
    onSuccess: async (confirmed) => {
      setBatch(confirmed);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['collection', 'venues'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'venue-import-rows'] }),
        queryClient.invalidateQueries({ queryKey: ['collection', 'venue-imports'] }),
      ]);
    },
  });
  const templateMutation = useMutation({
    mutationFn: () => downloadApiFile('/venue-imports/template/', accessToken ? { accessToken } : {}),
  });
  const errorsMutation = useMutation({
    mutationFn: () => downloadApiFile(
      `/venue-imports/${batch?.id}/errors/`, accessToken ? { accessToken } : {},
    ),
  });

  function toggleRow(rowId: string) {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  }

  function selectVisibleImportable() {
    setSelectedRows((current) => {
      const next = new Set(current);
      visibleRows.filter((row) => row.is_importable).forEach((row) => next.add(row.id));
      return next;
    });
  }

  const hasReviewRows = Boolean(batch && (
    batch.invalid_count + batch.exact_duplicate_count + batch.possible_duplicate_count + batch.conflict_count
  ));

  return <section className="content-card management-panel venue-import-panel">
    <div className="card-heading">
      <div><h2>Bulk import stores</h2><p>Validate a versioned CSV, review every result, then explicitly confirm selected rows.</p></div>
      <button aria-label="Close store import" className="icon-button" onClick={onClose} type="button"><X size={19} /></button>
    </div>

    {!batch ? <>
      <div className="venue-import-guide">
        <div><span className="icon-chip"><FileSpreadsheet size={18} /></span><strong>1. Use the current template</strong><p>Keep its headers and template version unchanged.</p></div>
        <div><span className="icon-chip"><Upload size={18} /></span><strong>2. Upload for preview</strong><p>No store is written during validation.</p></div>
        <div><span className="icon-chip"><Check size={18} /></span><strong>3. Review and confirm</strong><p>Only your selected eligible rows are imported.</p></div>
      </div>
      <button className="button secondary" disabled={templateMutation.isPending} onClick={() => templateMutation.mutate()} type="button">
        {templateMutation.isPending ? <InlineSpinner /> : <Download size={16} />} Download CSV template
      </button>
      <form className="venue-import-upload" onSubmit={(event: FormEvent) => { event.preventDefault(); previewMutation.mutate(); }}>
        <label>Store CSV<input accept=".csv,text/csv" aria-label="Store CSV" onChange={(event) => setFile(event.target.files?.[0] ?? null)} required type="file" /></label>
        <button className="button primary" disabled={!file || previewMutation.isPending} type="submit">
          {previewMutation.isPending ? <InlineSpinner /> : <Upload size={16} />}{previewMutation.isPending ? 'Validating…' : 'Preview import'}
        </button>
      </form>
      {templateMutation.isError || previewMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(templateMutation.error ?? previewMutation.error)}</p> : null}
      {(imports.data?.results.length ?? 0) > 0 ? <div className="venue-import-history"><h3>Recent store imports</h3><div className="data-table-wrap"><table className="data-table"><thead><tr><th>Import</th><th>File</th><th>Rows</th><th>Status</th><th><span className="sr-only">Action</span></th></tr></thead><tbody>{imports.data?.results.map((item) => <tr key={item.id}><td><strong>{item.reference}</strong><small>{new Date(item.created_at).toLocaleString('en-PK')}</small></td><td>{item.original_filename}</td><td>{item.status === 'confirmed' ? `${item.imported_count} imported` : `${item.total_rows} previewed`}</td><td><StatusPill value={item.status} /></td><td><button className="text-button" onClick={() => { setBatch(item); setPage(1); setClassification(''); setSelectedRows(new Set()); }} type="button">{item.status === 'confirmed' ? 'View' : 'Resume review'}</button></td></tr>)}</tbody></table></div></div> : null}
    </> : <>
      <div className="venue-import-meta">
        <div><span>Preview</span><strong>{batch.reference}</strong><small>{batch.original_filename} · expires {new Date(batch.expires_at).toLocaleString('en-PK')}</small></div>
        <StatusPill value={batch.status} />
      </div>
      <div aria-label="Store import summary" className="venue-import-summary">
        <div><span>Total</span><strong>{batch.total_rows}</strong></div>
        <div><span>Valid</span><strong>{batch.valid_count}</strong></div>
        <div><span>Possible duplicates</span><strong>{batch.possible_duplicate_count}</strong></div>
        <div><span>Exact duplicates</span><strong>{batch.exact_duplicate_count}</strong></div>
        <div><span>Invalid / conflicts</span><strong>{batch.invalid_count + batch.conflict_count}</strong></div>
      </div>
      {batch.status === 'confirmed' ? <><div className="success-banner"><Check size={18} /><span>Imported {batch.imported_count} stores. The catalogue and audit record are updated.</span></div><button className="button secondary" onClick={() => { setBatch(null); setFile(null); setSelectedRows(new Set()); }} type="button">Start another import</button></> : <>
        <div className="venue-import-toolbar">
          <label>Result<select aria-label="Import result" value={classification} onChange={(event) => { setClassification(event.target.value as VenueImportClassification | ''); setPage(1); }}><option value="">All results</option>{Object.entries(classificationLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <div className="table-actions"><button className="button secondary" onClick={selectVisibleImportable} type="button">Select eligible on this page</button>{hasReviewRows ? <button className="button secondary" disabled={errorsMutation.isPending} onClick={() => errorsMutation.mutate()} type="button"><Download size={15} /> Download review results</button> : null}</div>
        </div>
        <DataPanel loading={rows.isLoading} error={rows.isError} empty={visibleRows.length === 0}>
          <div className="data-table-wrap"><table className="data-table venue-import-table"><thead><tr><th><span className="sr-only">Select</span></th><th>CSV row</th><th>Store</th><th>Location</th><th>Result</th><th>Review detail</th></tr></thead><tbody>{visibleRows.map((row) => <tr key={row.id}><td><input aria-label={`Select CSV row ${row.row_number}`} checked={selectedRows.has(row.id)} disabled={!row.is_importable} onChange={() => toggleRow(row.id)} type="checkbox" /></td><td>{row.row_number}</td><td><strong>{row.data.name || 'Missing name'}</strong><small>{row.data.chain_name || row.data.chain_code || 'Independent'}{row.data.store_code ? ` · ${row.data.store_code}` : ''}</small></td><td>{row.data.address || 'Missing address'}<small>{row.data.city || 'Missing city'}, {row.data.country || 'Pakistan'}</small></td><td><StatusPill value={row.classification} />{row.classification === 'possible_duplicate' ? <small className="venue-import-warning"><AlertTriangle size={12} /> Select only after checking the match</small> : null}</td><td className="venue-import-detail">{issueText(row)}</td></tr>)}</tbody></table></div>
        </DataPanel>
        <div className="attendance-pagination"><button className="button secondary" disabled={page === 1} onClick={() => setPage((value) => value - 1)} type="button">Previous</button><span>Page {page} · {rows.data?.count ?? 0} rows</span><button className="button secondary" disabled={!rows.data?.next} onClick={() => setPage((value) => value + 1)} type="button">Next</button></div>
        {errorsMutation.isError || confirmMutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(errorsMutation.error ?? confirmMutation.error)}</p> : null}
        <div className="venue-import-confirm"><div><strong>{selectedRows.size} rows selected</strong><p>Confirmation is atomic. If the catalogue changed after preview, no selected row is imported.</p></div><button className="button primary" disabled={!selectedRows.size || batch.is_expired || confirmMutation.isPending} onClick={() => confirmMutation.mutate()} type="button">{confirmMutation.isPending ? <InlineSpinner /> : <Check size={16} />}{confirmMutation.isPending ? 'Importing…' : 'Confirm selected stores'}</button></div>
      </>}
    </>}
  </section>;
}
